import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { formatDate } from '../helpers';
import { BrandTag, ColourDot } from '../components/Badges';
import { showToast } from '../components/Toast';

const FILTERS = ['All', 'This Week', 'This Month', 'KIA', 'SUZUKI', 'GWM'];

function startOfWeekISO() {
  const d = new Date();
  const day = d.getDay();
  const toMon = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - toMon);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function Archive({ refreshKey, onRefresh }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('All');
  const [search, setSearch]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.archived({ limit: 200 })
      .then((v) => { if (!cancelled) setItems(v); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const filtered = useMemo(() => {
    let list = items;
    if (filter === 'This Week')  list = list.filter((v) => (v.archived_at || '').slice(0, 10) >= startOfWeekISO());
    if (filter === 'This Month') list = list.filter((v) => (v.archived_at || '').slice(0, 10) >= startOfMonthISO());
    if (['KIA', 'SUZUKI', 'GWM'].includes(filter)) list = list.filter((v) => v.brand === filter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        (v.stock_number || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q) ||
        (v.salesperson || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, filter, search]);

  async function unarchive(id) {
    if (!window.confirm('Move this vehicle back to active?')) return;
    try {
      await api.unarchive(id);
      showToast('Vehicle restored');
      onRefresh?.();
    } catch (e) { showToast('Failed: ' + e.message); }
  }

  if (loading) return <div className="loading">Loading archive…</div>;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="pt">Archive</div>
          <div className="ps">Completed & delivered vehicles — searchable database</div>
        </div>
      </div>

      <div className="fbar">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`fb ${filter === f ? 'on' : ''}`}
            onClick={() => setFilter(f)}
          >{f}</button>
        ))}
        <input
          className="fsrch"
          placeholder="Search stock #, model, salesperson..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="tw">
        <table className="vt">
          <thead>
            <tr>
              <th>Vehicle</th><th>Brand</th><th>Stock #</th><th>Colour</th>
              <th>Delivered</th><th>Services</th><th>Salesperson</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr className="empty"><td colSpan={8}>Archive is empty.</td></tr>
            ) : filtered.map((v) => (
              <tr key={v.id} className="row-even">
                <td><div className="vm">{v.model}</div></td>
                <td><BrandTag brand={v.brand} /></td>
                <td style={{ fontSize: 13, fontWeight: 700 }}>{v.stock_number}</td>
                <td><ColourDot colour={v.colour} /></td>
                <td>
                  <span className="arch-badge">
                    Delivered {v.archived_at ? formatDate(v.archived_at) : '—'}
                  </span>
                </td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {(v.services || []).join(' / ') || '—'}
                </td>
                <td style={{ fontSize: 12 }}>{v.salesperson || '—'}</td>
                <td><button className="ab" onClick={() => unarchive(v.id)}>Restore</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
